import { beginAdminRequest } from './requestLifecycle'

export interface AdminOperationView<TResult = unknown> {
  id: string
  type: string
  status: 'queued' | 'running' | 'succeeded' | 'failed_retryable' | 'failed_final' | 'cancelled' | 'expired'
  progress?: number
  version: number
  result?: TResult
  error?: { code: string; messageKey: string; traceId?: string }
  allowedActions: string[]
  terminalAt?: string
}

export interface MutationGuard {
  idempotencyKey: string
  reason: string
  ticket?: string
  accessGrantId?: string
  etag?: string
  stepUpToken?: string
}

export class AdminApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    public readonly traceId?: string,
    public readonly currentEtag?: string,
    public readonly details?: unknown,
    public readonly retryAfterSeconds?: number,
  ) {
    super(code)
  }
}

export function currentCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

export class AdminApiClient {
  constructor(private readonly baseUrl = import.meta.env.VITE_ADMIN_API_BASE ?? '') {}

  async request<T>(path: string, init: RequestInit = {}, guard?: MutationGuard, expectedStatus?: number, expectEmpty = false): Promise<T> {
    const headers = new Headers(init.headers)
    headers.set('Accept', 'application/json')
    headers.set('X-Request-ID', crypto.randomUUID())
    if (init.body) headers.set('Content-Type', 'application/json')
    if (guard) {
      if (!guard.reason.trim()) throw new AdminApiError('ADMIN_REASON_REQUIRED', 422)
      const csrf = currentCsrfToken()
      if (!csrf) throw new AdminApiError('ADMIN_CSRF_REQUIRED', 403)
      headers.set('X-CSRF-Token', csrf)
      headers.set('Idempotency-Key', guard.idempotencyKey)
      headers.set('X-Change-Reason', guard.reason.trim())
      if (guard.ticket) headers.set('X-Ticket-ID', guard.ticket.trim())
      if (guard.accessGrantId) headers.set('X-Access-Grant-ID', guard.accessGrantId.trim())
      if (guard.etag) headers.set('If-Match', guard.etag)
      if (guard.stepUpToken) headers.set('X-Step-Up-Token', guard.stepUpToken)
    }
    const request = beginAdminRequest(init.signal ?? undefined)
    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        ...init,
        headers,
        signal: request.signal,
        credentials: 'same-origin',
        cache: 'no-store',
      })
      request.assertCurrent()
      const rawBody = await response.text()
      let body: T | { code?: string; trace_id?: string } | null = null
      if (rawBody !== '') {
        try {
          body = JSON.parse(rawBody) as T | { code?: string; trace_id?: string }
        } catch {
          body = null
        }
      }
      request.assertCurrent()
      const statusMismatch = expectedStatus !== undefined && response.status !== expectedStatus
      const bodyMismatch = expectEmpty && rawBody.length !== 0
      if (!response.ok || statusMismatch || bodyMismatch) {
        const error = body as { code?: string; trace_id?: string } | null
        if (response.status === 401 && !path.startsWith('/admin/v1/auth/') && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('bondpet-admin-auth-required'))
        }
        const retryAfter = Number.parseInt(response.headers.get('Retry-After') ?? '', 10)
        throw new AdminApiError(error?.code ?? (statusMismatch || bodyMismatch ? 'ADMIN_UNEXPECTED_RESPONSE' : 'ADMIN_REQUEST_FAILED'), response.status, error?.trace_id, response.headers.get('ETag') ?? undefined, body, Number.isFinite(retryAfter) ? retryAfter : undefined)
      }
      return body as T
    } finally {
      request.release()
    }
  }

  get<T>(path: string, signal?: AbortSignal) {
    return this.request<T>(path, { method: 'GET', signal })
  }

  mutate<TRequest, TResponse>(method: 'POST' | 'PATCH' | 'PUT' | 'DELETE', path: string, body: TRequest, guard: MutationGuard, signal?: AbortSignal) {
    return this.request<TResponse>(path, { method, signal, body: JSON.stringify(body) }, guard)
  }

  getOperation<TResult>(id: string, signal?: AbortSignal) {
    return this.get<AdminOperationView<TResult>>(`/admin/v1/operations/${encodeURIComponent(id)}`, signal)
  }

  cancelOperation(id: string, expectedVersion: number, guard: MutationGuard, signal?: AbortSignal) {
    return this.mutate<{ reason: string; expected_version: number }, AdminOperationView>('POST', `/admin/v1/operations/${encodeURIComponent(id)}/cancellations`, {
      reason: guard.reason,
      expected_version: expectedVersion,
    }, guard, signal)
  }

  retryOperation(id: string, expectedVersion: number, guard: MutationGuard, signal?: AbortSignal) {
    return this.mutate<{ reason: string; expected_version: number }, AdminOperationView>('POST', `/admin/v1/operations/${encodeURIComponent(id)}/retries`, {
      reason: guard.reason,
      expected_version: expectedVersion,
    }, guard, signal)
  }
}

export const adminApi = new AdminApiClient()

export async function pollOperation<TResult>(id: string, signal: AbortSignal, onUpdate: (view: AdminOperationView<TResult>) => void) {
  let delay = 600
  while (!signal.aborted) {
    const view = await adminApi.getOperation<TResult>(id, signal)
    if (signal.aborted) throw signal.reason
    onUpdate(view)
    if (!['queued', 'running'].includes(view.status)) return view
    await new Promise<void>((resolve, reject) => {
      if (signal.aborted) return reject(signal.reason)
      const onTimer = () => {
        signal.removeEventListener('abort', onAbort)
        resolve()
      }
      const timer = window.setTimeout(onTimer, delay)
      const onAbort = () => {
        window.clearTimeout(timer)
        signal.removeEventListener('abort', onAbort)
        reject(signal.reason)
      }
      signal.addEventListener('abort', onAbort, { once: true })
    })
    delay = Math.min(Math.round(delay * 1.6), 5000)
  }
  throw signal.reason
}
